<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Locales</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">
          <div class="w-full w-md-auto md-flex-1">
            <div class="d-flex justify-content-between align-items-center justify-content-lg-start">
              <button type="button" class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters">
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>

              <div class="d-md-none">
                <button type="button" class="button-tooltip text-dark font-weight-bold m-0" v-b-tooltip.hover
                  title="Recargar" @click="loadData()">
                  <i class="ri-loop-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0">
            <div class="d-none d-md-flex">
              <button type="button" class="button-tooltip text-dark font-weight-bold" v-b-tooltip.hover title="Recargar"
                @click="loadData()">
                <i class="ri-loop-right-line"></i>
              </button>
            </div>

            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <SearchBar placeholder="Buscar por nombre o dirección" @search="onSearch"
                @input="search = $event" class="flex-1" />

              <div class="text-right mt-2 mt-md-0">
                <b-button :to="{ name: 'location-create' }" variant="none"
                  class="button button-primary ml-md-2">Agregar</b-button>
              </div>
            </div>
          </section>

          <filter-menu :show="showFilters" @hide="showFilters = false" :options="filterOptions"
            :selectedOptionFilter="filters" @setFilters="setFilters"></filter-menu>
        </div>

        <div class="w-100 d-flex flex-wrap align-items-center mt-2" v-if="showFiltersList">
          <span v-for="(val, name) in filterList" v-bind:key="name" class="filter-item">
            {{ getFilterName(name, val) }}
            <button type="button" class="text-white p-0 bg-transparent border-0 ml-2" @click="deleteFilter(name)">
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <div>
        <!-- Mobile -->
        <div class="custom-table-mobile d-xl-none">
          <section class="custom-table-mobile-header">
            <div>
              <span>Nombre</span>
            </div>

            <div>
              <span>Dirección</span>
            </div>
          </section>

          <div class="custom-table-mobile-body">
            <div class="custom-table-mobile-row" v-for="o in paginatedList" :key="o.id">
              <div class="custom-table-mobile-grid">
                <div>
                  <span>{{ o.name }}</span>
                </div>

                <div>
                  <span>{{ o.address }}</span>
                </div>

                <div>
                  <button type="button" class="border-0 bg-transparent" @click="o.showMobileData = !o.showMobileData">
                    <span v-if="!o.showMobileData">
                      <i class="ri-arrow-down-s-line"></i>
                    </span>
                    <span v-else>
                      <i class="ri-arrow-up-s-line"></i>
                    </span>
                  </button>
                </div>
              </div>

              <div class="custom-table-mobile-options mt-3">
                <router-link :to="{ name: 'location-edit', params: { id: o.id } }" class="mr-2">
                  Editar
                </router-link>

                <a href="#" @click.prevent="openChangeStatus(o)" class="mr-2">{{ getStatus(o.status).action }}</a>

                <a href="#" @click.prevent="openDelete(o)">Eliminar</a>
              </div>

              <div class="pt-3" v-if="o.showMobileData">
                <ul class="list-unstyled p-0">
                  <li>
                    <span class="font-weight-medium d-inline-block mr-2">Estado:</span>
                    <span :class="`badge ${getStatus(o.status).color}`">{{ getStatus(o.status).label }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop -->
        <table class="custom-table w-100 d-none d-xl-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Dirección</th>
              <th>Estado</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in paginatedList" :key="o.id">
              <td class="pl-3">{{ o.name }}</td>
              <td>{{ o.address }}</td>
              <td class="text-center">
                <span :class="`badge ${getStatus(o.status).color}`">{{ getStatus(o.status).label }}</span>
              </td>
              <td class="text-center d_option pr-md-3">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem @click="$router.push({ name: 'location-edit', params: { id: o.id } })"
                      text="Editar" />
                    <DropdownItem :text="getStatus(o.status).action" @click="openChangeStatus(o)" />
                    <DropdownItem text="Eliminar" @click="openDelete(o)" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <pagination :total="total" :page="page" :page-size="pageSize" :pages="pages" :changePage="changePage"
          :changePageSize="changePageSize" v-if="filteredList.length" />

        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </div>
    </div>

    <ConfirmationModal :show="confirmationModal.show" @hide="closeConfirmationModal" @confirm="confirmModal"
      :data="confirmationModal" />
  </div>
</template>

<script>
import { LocationService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

import {
  Alert,
  Constants,
  Util,
  PaginationMixin,
  DropdownTable,
  DropdownItem,
  SearchBar,
  FilterMenu,
  FilterMixin,
  ConfirmationModal,
  ConfirmationModalMixin
} from "wize-admin";
import { mapGetters } from "vuex";

export default {
  components: {
    SearchBar,
    FilterMenu,
    DropdownTable,
    DropdownItem,
    ConfirmationModal
  },
  mixins: [PaginationMixin, FilterMixin, ConfirmationModalMixin],
  data() {
    return {
      list: [],
      confirmedSearch: null,
      filterOptions: [
        {
          label: "Estado",
          key: "status",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.LocationStatus.ACTIVE,
            LocalConstants.LocationStatus.INACTIVE,
          ],
        },
      ],
    };
  },
  methods: {
    onSearch(value) {
      this.confirmedSearch = value;
      this.page = 1;
    },
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);
        const response = await LocationService.list(this.business.id);
        this.list = response.payload.map((item) => ({
          ...item,
          showMobileData: false
        }));
      } catch (error) {
        Alert.error(error);
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    getStatus(key) {
      return Util.searchValue(key, LocalConstants.LocationStatus);
    },
    openChangeStatus(o) {
      this.selectedOption = o;
      this.openConfirmationModal({
        title: "Cambiar estado",
        description: "¿Está seguro que desea cambiar el estado?",
        acceptText: "Aceptar",
        action: "update"
      });
    },
    openDelete(o) {
      this.selectedOption = o;
      this.openConfirmationModal({ acceptText: "Borrar", action: "delete" });
    },
    confirmModal() {
      this.closeConfirmationModal();
      if (this.confirmationModal.action === "update") this.changeStatus();
      if (this.confirmationModal.action === "delete") this.deleteItem();
    },
    async changeStatus() {
      try {
        await this.$store.dispatch("app/loading", true);
        const currentStatus = this.selectedOption.status;
        const newStatus = currentStatus === LocalConstants.LocationStatus.ACTIVE.key
          ? LocalConstants.LocationStatus.INACTIVE.key
          : LocalConstants.LocationStatus.ACTIVE.key;

        const resp = (
          await LocationService.toggleStatus(this.business.id, this.selectedOption.id, newStatus)
        ).payload;

        const item = this.list.find((o) => o.id === resp.id);
        if (item) {
          const index = this.list.indexOf(item);
          this.$set(this.list, index, { ...resp, showMobileData: false });
        }
        Alert.success("Estado actualizado correctamente");
      } catch (ex) {
        console.error(ex);
        Alert.error(ex);
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async deleteItem() {
      try {
        await this.$store.dispatch("app/loading", true);
        await LocationService.delete(this.business.id, this.selectedOption.id);
        Alert.success("Local eliminado correctamente");
        await this.loadData();
      } catch (ex) {
        console.error(ex);
        Alert.error(ex);
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    }
  },
  computed: {
    ...mapGetters({
      business: "app/getBusiness"
    }),
    filteredList() {
      let result = this.list;

      // Filtro por búsqueda (solo al presionar Enter)
      if (this.confirmedSearch) {
        const searchLower = this.confirmedSearch.toLowerCase();
        result = result.filter((item) =>
          (item.name && item.name.toLowerCase().includes(searchLower)) ||
          (item.address && item.address.toLowerCase().includes(searchLower))
        );
      }

      // Filtro por estado
      if (this.filters && this.filters.status) {
        result = result.filter((item) => item.status === this.filters.status);
      }

      return result;
    },
    paginatedList() {
      const start = (this.page - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredList.slice(start, end);
    }
  },
  watch: {
    filteredList: {
      handler(val) {
        this.total = val.length;
        this.pages = Math.ceil(val.length / this.pageSize) || 1;

        if (this.page > this.pages) {
          this.page = 1;
        }
      },
      immediate: true
    },
    filters: {
      handler() {
        this.page = 1;
      },
      deep: true
    }
  },
  async mounted() {
    if (this.business) {
      await this.loadData();
    }
  }
};
</script>

<style lang="stylus" scoped>
</style>